import React from 'react';
import {
  Box,
  Typography,
  Stack,
  IconButton,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { APP_CONFIG } from '../../../../config/app.config';

interface WarehouseDataColumnMappingAreaProps {
  columnMapping?: {
    ean?: string;
    quantity?: string;
  };
  rows: string[][];
  eanColumnIndex: number;
  quantityColumnIndex: number;
  draggedColumn: string | null;
  dragOverTarget: 'ean' | 'quantity' | null;
  highlightDestinations: boolean;
  onDragOver: (e: React.DragEvent, target: 'ean' | 'quantity') => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, target: 'ean' | 'quantity') => void;
  onColumnMappingChange: (field: 'ean' | 'quantity', columnName: string) => void;
}

const WarehouseDataColumnMappingArea: React.FC<WarehouseDataColumnMappingAreaProps> = ({
  columnMapping,
  rows,
  eanColumnIndex,
  quantityColumnIndex,
  draggedColumn,
  dragOverTarget,
  highlightDestinations,
  onDragOver,
  onDragLeave,
  onDrop,
  onColumnMappingChange,
}) => {
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      width: '400px',
      border: '1px solid',
      borderColor: APP_CONFIG.ui.theme.colors.border,
      backgroundColor: APP_CONFIG.ui.theme.colors.backgroundTint,
    }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', p: 3, pb: 0 }}>
        <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
          Map your EAN and Quantity fields
        </Typography>
      </Box>

      <Stack sx={{
        p: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'row',
        gap: 3
      }}>
        {/* EAN Target */}
        <Box
          onDragOver={(e) => onDragOver(e, 'ean')}
          onDragLeave={onDragLeave}
          onDrop={(e) => onDrop(e, 'ean')}
          sx={{
            minWidth: '150px',
            maxWidth: '200px',
            flex: 1,
            height: '100%',
            border: columnMapping?.ean ? '2px solid' : '2px dashed',
            borderColor: columnMapping?.ean ? 'secondary.main' : 'primary.main',
            borderRadius: 1,
            backgroundColor: columnMapping?.ean ? 'primary.50' : (dragOverTarget === 'ean' || draggedColumn || highlightDestinations) ? 'primary.50' : 'white',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: columnMapping?.ean
              ? '0 4px 12px rgba(25, 118, 210, 0.3)'
              : (dragOverTarget === 'ean' || draggedColumn || highlightDestinations)
                ? '0 4px 12px rgba(25, 118, 210, 0.2)'
                : 'none',
            transform: columnMapping?.ean ? 'scale(1.00)' : (dragOverTarget === 'ean' || draggedColumn || highlightDestinations) ? 'scale(1.02)' : 'scale(1)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: columnMapping?.ean
                ? '0 6px 16px rgba(25, 118, 210, 0.4)'
                : '0 4px 12px rgba(0, 0, 0, 0.15)',
              transform: 'scale(1.02)',
            },
          }}
        >
          {/* Column Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid',
              borderColor: APP_CONFIG.ui.theme.colors.border,
              backgroundColor: columnMapping?.ean ? 'secondary.main' : 'primary.main',
              fontWeight: 'bold',
              textAlign: 'left',
              position: 'relative',
              cursor: columnMapping?.ean ? 'pointer' : 'default',
              '&:hover': columnMapping?.ean ? {
                backgroundColor: 'secondary.main',
                '& .remove-button': {
                  opacity: 1,
                }
              } : {},
            }}
            onClick={() => {
              if (columnMapping?.ean) {
                onColumnMappingChange('ean', '');
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'white',
              }}
            >
              EAN Column
            </Typography>

            {columnMapping?.ean && (
              <IconButton
                className="remove-button"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onColumnMappingChange('ean', '');
                }}
                sx={{
                  position: 'absolute',
                  top: 4,
                  left: 4,
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  }
                }}
              >
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>

          {/* Column Data */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {columnMapping?.ean ? (
              rows.map((row, rowIndex) => {
                const cellValue = row[eanColumnIndex] || '';

                return (
                  <Box
                    key={rowIndex}
                    sx={{
                      p: 2,
                      borderBottom: rowIndex < rows.length - 1 ? '1px solid' : 'none',
                      borderColor: APP_CONFIG.ui.theme.colors.border,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                        textAlign: 'left',
                      }}
                    >
                      {cellValue}
                    </Typography>
                  </Box>
                );
              })
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Drag a column here
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Quantity Target */}
        <Box
          onDragOver={(e) => onDragOver(e, 'quantity')}
          onDragLeave={onDragLeave}
          onDrop={(e) => onDrop(e, 'quantity')}
          sx={{
            height: '100%',
            minWidth: '150px',
            maxWidth: '200px',
            flex: 1,
            border: columnMapping?.quantity ? '2px solid' : '2px dashed',
            borderColor: columnMapping?.quantity ? 'secondary.main' : 'primary.main',
            borderRadius: 2,
            backgroundColor: columnMapping?.quantity ? 'primary.50' : (dragOverTarget === 'quantity' || draggedColumn || highlightDestinations) ? 'primary.50' : 'white',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            boxShadow: columnMapping?.quantity
              ? '0 4px 12px rgba(25, 210, 210, 0.3)'
              : (dragOverTarget === 'quantity' || draggedColumn || highlightDestinations)
                ? '0 4px 12px rgba(25, 118, 210, 0.2)'
                : 'none',
            transform: columnMapping?.quantity ? 'scale(1.00)' : (dragOverTarget === 'quantity' || draggedColumn || highlightDestinations) ? 'scale(1.02)' : 'scale(1)',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: columnMapping?.quantity
                ? '0 6px 16px rgba(25, 118, 210, 0.4)'
                : '0 4px 12px rgba(0, 0, 0, 0.15)',
              transform: 'scale(1.02)',
            },
          }}
        >
          {/* Column Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: '1px solid',
              borderColor: APP_CONFIG.ui.theme.colors.border,
              backgroundColor: columnMapping?.quantity ? 'secondary.main' : 'primary.main',
              fontWeight: 'bold',
              textAlign: 'left',
              position: 'relative',
              cursor: columnMapping?.quantity ? 'pointer' : 'default',
              '&:hover': columnMapping?.quantity ? {
                backgroundColor: 'secondary.main',
                '& .remove-button': {
                  opacity: 1,
                }
              } : {},
            }}
            onClick={() => {
              if (columnMapping?.quantity) {
                onColumnMappingChange('quantity', '');
              }
            }}
          >
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: 'white',
              }}
            >
              Quantity Column
            </Typography>

            {columnMapping?.quantity && (
              <IconButton
                className="remove-button"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onColumnMappingChange('quantity', '');
                }}
                sx={{
                  position: 'absolute',
                  top: 4,
                  left: 4,
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  }
                }}
              >
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>

          {/* Column Data */}
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {columnMapping?.quantity ? (
              rows.map((row, rowIndex) => {
                const cellValue = row[quantityColumnIndex] || '';

                return (
                  <Box
                    key={rowIndex}
                    sx={{
                      p: 2,
                      borderBottom: rowIndex < rows.length - 1 ? '1px solid' : 'none',
                      borderColor: APP_CONFIG.ui.theme.colors.border,
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        width: '100%',
                        textAlign: 'left',
                      }}
                    >
                      {cellValue}
                    </Typography>
                  </Box>
                );
              })
            ) : (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Drag a column here
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export default WarehouseDataColumnMappingArea; 